import {
  Box,
  Button,
  Typography,
  Hidden,
  Grid,
  IconButton,
  Tooltip,
  Stack,
  Switch,
  CardMedia,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useEffect, useMemo, useState, ChangeEvent } from 'react';
import EditIcon from '@mui/icons-material/Edit';
import { useHistory } from 'react-router';
import { useDispatch } from 'react-redux';
import { getPoaApproverList, updatePOAApprover } from '../../redux-store/actions/userManagement';
import { AddPoaApproverRequestBody, poaApprover } from '../../redux-store/types/api-types';
import { Link as RouterLink } from 'react-router-dom';
import { DataTable } from '../DataTable';
import { Location } from 'history';
import { ConfirmationDialog } from '../commonComponents';
import { AccessHeader } from './relationShipManagers';

export default function PoaApprovers({
  location,
}: {
  location: Location<{ distributorId: number }>;
}): JSX.Element {
  const dispatch = useDispatch();
  const [isLoading, setLoading] = useState(false);
  const { distributorId } = location.state || {};
  const [poaApproverData, setpoaApproverData] = useState<poaApprover[]>([]);
  const history = useHistory();
  const [currentPage, setCurrentPage] = useState(0);
  const [openDialog, setDialog] = useState<{
    poaRowData: AddPoaApproverRequestBody;
  } | null>(null);

  const headers = [
    {
      header: `Name`,
      renderCell: (row: poaApprover) => {
        return (
          <Typography
            variant="h5"
            sx={{
              color: '#61D1D6',
              textDecoration: 'none',
              fontWeight: 500,
              fontSize: '14px',
              ':hover': {
                color: 'primary.main',
              },
            }}
            component={RouterLink}
            to={{
              pathname: 'poa-approver-details',
              state: { poaApproverId: row.id },
            }}>
            {row.user?.firstName || 'N/A'}
          </Typography>
        );
      },
    },
    {
      header: 'Mobile Number',
      valueGetter: (row: poaApprover) => row.user.phone || 'N/A',
    },
    {
      header: 'Email Id',
      valueGetter: (row: poaApprover) => row.user.email || 'N/A',
    },
    {
      header: AccessHeader,
      renderCell: (row: AddPoaApproverRequestBody): JSX.Element => {
        return (
          <>
            <Stack
              sx={{
                alignItems: { xs: 'baseline', sm: 'center' },
                '.MuiSwitch-root': {
                  paddingLeft: { xs: '0px', sm: '12px' },
                  width: { xs: '52px', sm: '58px' },
                },
                '.MuiSwitch-switchBase': { paddingLeft: { xs: '0px', sm: '9px' } },
              }}>
              <Switch
                sx={{ '.MuiSwitch-switchBase': { color: 'black' } }}
                checked={row.isActive}
                onChange={() => setDialog({ poaRowData: row })}
              />
            </Stack>
          </>
        );
      },
    },
    // {
    //   header: '',
    //   renderCell: (row: poaApprover): JSX.Element => {
    //     return (
    //       <>
    //         {row.isActive ? (
    //           <Tooltip title="Edit">
    //             <IconButton
    //               component={RouterLink}
    //               to={{
    //                 pathname: 'edit-poaApprover',
    //                 state: { poaApproverId: row.id },
    //               }}
    //               sx={{
    //                 ':hover': {
    //                   bgcolor: '#F4FCFC',
    //                 },
    //                 '&.Mui-disabled': {
    //                   cursor: 'pointer',
    //                   pointerEvents: 'all',
    //                 },
    //               }}>
    //               <EditIcon />
    //             </IconButton>
    //           </Tooltip>
    //         ) : (
    //           <Typography
    //             variant="h5"
    //             component={RouterLink}
    //             to={{
    //               pathname: 'edit-poaApprover',
    //               state: { poaApproverId: row.id },
    //             }}
    //             sx={{
    //               color: '#61D1D6',
    //               textDecoration: 'none',
    //               fontWeight: 500,
    //               fontSize: '14px',
    //               ':hover': {
    //                 color: 'primary.main',
    //               },
    //             }}>
    //             View Details
    //           </Typography>
    //         )}
    //       </>
    //     );
    //   },
    // },
  ];

  useEffect(() => {
    let isComponentActive = true;
    (async function () {
      try {
        setLoading(true);
        if (distributorId) {
          const res = (await dispatch(
            getPoaApproverList({ distributorId: distributorId })
          )) as unknown as poaApprover[];
          setpoaApproverData(res);
        } else {
          history.replace('/user-management');
        }
        if (!isComponentActive) {
          return;
        }
        setLoading(false);
      } catch (e) {
        console.log((e as Error).message);
      } finally {
        if (isComponentActive) setLoading(false);
      }
    })();
    return () => {
      isComponentActive = false;
    };
  }, []);

  const handlePageChange = (event: ChangeEvent<unknown>, value: number) => {
    setCurrentPage(value);
  };

  const additionalCell = {
    header: '',
    renderEditDeleteCell: (row: poaApprover): JSX.Element => {
      const [tooltip, setTooltip] = useState('Click to Copy');
      return (
        <Box display="flex">
          {row?.user?.resetReferenceId && (
            <Tooltip title={tooltip}>
              <IconButton
                onMouseOver={() => {
                  setTooltip('Copy Link');
                }}
                onClick={() => {
                  setTooltip('Copied'),
                    navigator.clipboard.writeText(
                      `${window.location.origin}/reset/${row?.user?.resetReferenceId}`
                    );
                }}>
                <CardMedia
                  component="img"
                  src="/images/link-icon.svg"
                  alt="Link"
                  sx={{ width: '35px' }}
                />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      );
    },
  };

  return (
    <>
      <Box sx={{ bgcolor: 'common.white', px: 4 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            pt: { xs: 2, sm: 7 },
            pb: { xs: 2, sm: 4 },
          }}>
          <Typography sx={{ fontSize: { xs: 15, sm: 23 }, fontWeight: 500 }}>
            List of POA Approvers
          </Typography>
          <Hidden only="xs">
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              sx={{
                color: 'white',
                fontWeight: 500,
                fontSize: 14,
                mr: 2.5,
                py: 0.5,
                px: 3,
              }}
              onClick={() =>
                history.push('add-poaApprover', { distributorId: distributorId.toString() })
              }>
              Add POA Approvers
            </Button>
          </Hidden>
          <Hidden smUp={true}>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              sx={{
                color: 'primary.main',
                fontWeight: 500,
                fontSize: 13,
                // mr: 2.5,
                py: 0.5,
                // px: 3,
              }}
              onClick={() => {
                history.push('add-poaApprover', { distributorId: distributorId.toString() });
              }}>
              Add POA Approvers
            </Button>
          </Hidden>
        </Box>
        <Grid item xs={12} sm={12} lg={12}>
          {useMemo(
            () => (
              <DataTable
                tableData={poaApproverData}
                tableHeader={[...headers, additionalCell]}
                isLoading={isLoading}
                currentPage={currentPage}
                noOfPages={1}
                paginationAction={handlePageChange}
                renderAdditionalRow={true}
                tableBodyCustomStyles={{
                  '.MuiTableCell-root': {
                    borderBottom: 'none',
                  },
                }}
                tableHeaderCustomStyles={{
                  '&:last-child th': { border: 0 },
                  '.MuiTableCell-head': {
                    fontFamily: 'Poppins, sans-serif',
                    fontSize: 14,
                    fontWeight: 500,
                    color: 'primary.main',
                  },
                }}
              />
            ),
            [headers, isLoading, poaApproverData]
          )}
        </Grid>
      </Box>
      <ConfirmationDialog
        message={`Are you sure you want to ${
          openDialog?.poaRowData.isActive ? 'disable' : 'enable'
        } the access to this user ?`}
        open={openDialog !== null}
        setOpen={() => setDialog(null)}
        onSave={async () => {
          setDialog(null);
          try {
            if (openDialog) {
              const updatedResponse = (await dispatch(
                updatePOAApprover(openDialog.poaRowData.id as number, {
                  ...openDialog.poaRowData,
                  isActive: !openDialog?.poaRowData.isActive,
                })
              )) as unknown as poaApprover;
              setpoaApproverData(
                poaApproverData?.map((poaApprover) => {
                  if (Number(poaApprover.id) === Number(updatedResponse.id)) {
                    return {
                      ...poaApprover,
                      ...updatedResponse,
                    };
                  }
                  return poaApprover;
                })
              );
            }
          } catch (e) {
            console.error((e as Error).message);
          }
        }}
        onCancel={() => setDialog(null)}
      />
    </>
  );
}
